/* eslint-disable max-len */
import React, { forwardRef, memo } from 'react';
import PropTypes from 'prop-types';
import Promise from 'bluebird';
import _ from 'underscore';
import classnames from 'classnames';
import $ from 'jquery';

import { getAdCreatorInstrumentation } from '@bingads-webui-campaign/ad-creator-tools-instrumentation';
import { Carousel } from '@bingads-webui-react/primitive-unthemed';
import { TooltipHost, ActionButton } from '@bingads-webui-react/fabric-control';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import {
  attachAIGenAssetContextualAttribute,
} from '@bingads-webui-campaign/responsive-search-ad-strength';
import { PlayPauseButtons, isPlayPausePreviewImageEnabled } from '@bingads-webui-campaign-react/play-pause-buttons';

import getCroppedImage from '../util/get-cropped-image';
import getGenericImageObjectAsync from '../util/get-generic-image-object';
import { getPreview, getPreviewByType } from './preview-factory';
import { OutlookPreview } from './outlook-preview';
import C from '../constants';
import { MultiImageGeneral } from './multi-image-general';
import { ImageExperienceImage } from '../model/image-experience-image';
import { StyledAdPlacementsGridPreviewPanel } from './styled-ad-placements-grid-preview-panel';
import { getPreviewCarouselStyle } from '../../style/js/preview-carousel-style';

const multiImageAspectRatio = C.aspectRatios.Image133x100.type;
const bingSingleImageAspR = C.aspectRatios.Image178x100.type;

export class UnStyledPreviewCarousel extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      index: 0,
      direction: null,
      defaultImages: [],
      placeHolderImages: [],
      imageListForDisplay: [],
      aspectRatios: props.isMultimediaAd ? C.getMmaAspectRatios() : C.aspectRatios,
      isViewAllPanelOpen: false,
      auto: this.props.enableAutoRotate,
    };

    this.isMultimediaAd = props.isMultimediaAd;
    this.isMultiMediaAdsPhase2Enabled = props.permissions.IsMultiMediaAdsPhase2Enabled;
    this.isRecommendationTypeImproveMultiMediaAdsEnabled = true;
    this.aspectRatiosOrder = C.getImageTypesWithOrder(this.isMultimediaAd);
    this.setupImagesAsync();
    this.setupAdPreviewIndexToTitlesMap();
    this.adPreviewIndexToTitleInViewAll =
      this.setupAdPreviewIndexToTitlesMapInViewAll('ImageExperience_Ad_Preview_Ratio_Title_Template');

    // image extension features are being released after AdCreatorV2 has been GAed
    this.enableCreatorFeatures =
      (this.props.isAdCreatorEnabled && !this.props.isImageAdExtensionMode) || (this.props.isImageAdExtensionMode && this.props.isImageExtensionsEnhancementsEnabled);
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    const imageListForDisplay = _.map(prevState.defaultImages, (defaultImage) => {
      const imgSet = _.find(nextProps.imageList, imageObj => imageObj.Type === defaultImage.Type);

      if (imgSet) {
        return imgSet;
      }

      return defaultImage;
    });

    const selectedImage = imageListForDisplay[prevState.index];

    if (_.result(selectedImage, 'isLogo')) {
      _.each(imageListForDisplay, (imageObj) => {
        imageObj.isLogo = true; // eslint-disable-line no-param-reassign
      });
    }

    const aspectRatios = nextProps.isMultimediaAd ?
      C.getMmaAspectRatios(_.result(selectedImage, 'isLogo') ?
        C.imageOptionKeys.Logo : C.imageOptionKeys.Image) : C.aspectRatios;

    const multiImageList = [];

    // fallback to default images if placeholder not provided
    const placeHolderImages = prevState.placeHolderImages.length > 0 ?
      [...prevState.placeHolderImages] :
      [imageListForDisplay, imageListForDisplay];

    if (nextProps.isImageAdExtensionMode) {
      const allImages = [imageListForDisplay, ...placeHolderImages];

      allImages.forEach((imageObjs, idx) => {
        const image133x100 = _.find(imageObjs, img => img.type === multiImageAspectRatio);

        if (image133x100) {
          const cloned = image133x100.clone();

          cloned.setNewId(cloned.id ? cloned.id + idx : idx);

          multiImageList.push(cloned);
        }
      });
    }

    return {
      imageListForDisplay,
      multiImageList,
      aspectRatios,
    };
  }

  componentDidMount() {
    this.setAutoRotate();
  }

  componentDidUpdate() {
    if (_.result(_.first(this.state.imageListForDisplay), 'isRecommendedImage')) {
      // Registers AI generated asset contextual attribute popup to listen for key/mouse events
      const element = $('.iconba.iconba-Lightbulb.new-preview-carousel');
      attachAIGenAssetContextualAttribute(_.extend({
        element,
        i18n: this.props.i18n,
        improveMMAOption: { type: 'Image' },
        newI18n: this.props.newI18n,
      }));
    }
  }

  componentWillUnmount() {
    this.clearAutoRotateListener();
  }

  onAddAspectRatio = (e) => {
    const index = this.getEditingImageIndex(e);
    const {
      imageListForDisplay,
    } = this.state;

    this.props.onAddAspectRatio(imageListForDisplay[index].type);
  };

  onCrop = (e) => {
    const index = this.getEditingImageIndex(e);
    const {
      imageListForDisplay,
    } = this.state;

    if (this.enableCreatorFeatures) {
      this.setState({ isViewAllPanelOpen: false }, () => {
        this.props.onCropImage(imageListForDisplay[index]);
      });
    } else {
      this.props.onCropImage(imageListForDisplay[index]);
    }

    if (this.props.isAdCreatorEnabled) {
      const { editPlacementActivity } =
        getAdCreatorInstrumentation(this.props.instrumentation, this.props.adCreatorScenario);

      editPlacementActivity.trace('User opened edit placement panel');
    }
  };

  onReplace = (e) => {
    const index = this.getEditingImageIndex(e);
    const {
      imageListForDisplay,
    } = this.state;

    this.setState({ isViewAllPanelOpen: false }, () => {
      this.props.onReplaceImage(imageListForDisplay[index]);
    });
  };

  onRemoveAspectRatio = (e) => {
    const index = this.getEditingImageIndex(e);
    const {
      imageListForDisplay,
    } = this.state;

    this.props.onRemoveAspectRatio(imageListForDisplay[index].type);
  };

  setAutoRotate = () => {
    // disable auto-rotate in test
    /* istanbul ignore if */
    if (window.e2eTestEnvironment) {
      return;
    }

    if (this.state.auto === false) {
      return;
    }

    this.clearAutoRotateListener();
    const { index, imageListForDisplay } = this.state;

    const indicators = document.querySelector('.new-preview-carousel-container .carousel-indicators');

    // make sure we have the correct preview to rotate
    const previewCount = (indicators && indicators.childElementCount) || imageListForDisplay.length;
    let nextIndex = index + 1;

    /* istanbul ignore next */
    if (nextIndex >= previewCount) {
      nextIndex = 0;
    }

    this.autoRotateListener = setTimeout(() => {
      /* istanbul ignore next */
      if (this.props.enableAutoRotate) {
        this.setState({
          index: nextIndex,
          direction: 'next',
        });
      }

      /* istanbul ignore next */
      this.setAutoRotate();
    }, 3000);
  };

  getEditingImageIndex(e) {
    return _.isUndefined(e.target.dataset.index)
      ? this.state.index : parseInt(e.target.dataset.index, 10);
  }

  setPreviewIndexByType(type) {
    const currentIndex = this.adPreviewTypesOrdered.indexOf(type);

    if (currentIndex >= 0) {
      this.setState({
        index: currentIndex,
      });
    }
  }

  setupAdPreviewIndexToTitlesMap() {
    this.adPreviewIndexToTitle = [];
    this.adPreviewTypesOrdered = [];

    const multiTitle = this.props.i18n.getString(
      'ImageExperience_Ad_Preview_Title_Template',
      {
        // eslint-disable-next-line
        ratio: this.props.i18n.getString(C.aspectRatios[multiImageAspectRatio].stringKey),
      }
    );

    const singleExtensionTitle = this.props.i18n.getString(
      'ImageExperience_Ad_Preview_Title_Template',
      {
        // eslint-disable-next-line
        ratio: this.props.i18n.getString(C.aspectRatios[bingSingleImageAspR].stringKey),
      }
    );

    const responsivePreviewsTitles = _.map(this.aspectRatiosOrder, typeKey => this.props.i18n.getString(
      'ImageExperience_Ad_Preview_Title_Template',
      {
        // eslint-disable-next-line
          ratio: this.props.i18n.getString(this.state.aspectRatios[typeKey].stringKey),
      }
    ));

    const genericTitle = this.props.i18n.getString('ImageExperience_Ad_Preview_Title');

    if (this.props.isImageAdExtensionMode && !this.isMultimediaAd) {
      this.adPreviewIndexToTitle.push(singleExtensionTitle);
      this.adPreviewIndexToTitle.push(multiTitle);
      this.adPreviewIndexToTitle.push(multiTitle);

      this.adPreviewTypesOrdered.push(C.aspectRatios.Image178x100.type);
      this.adPreviewTypesOrdered.push(C.aspectRatios.Image133x100.type);
      this.adPreviewTypesOrdered.push(C.aspectRatios.Image133x100.type);
    }

    this.adPreviewIndexToTitle = this.adPreviewIndexToTitle.concat(responsivePreviewsTitles);
    this.adPreviewTypesOrdered = this.adPreviewTypesOrdered.concat(this.aspectRatiosOrder);

    if (!this.props.isImageAdExtensionMode && !this.isMultimediaAd) {
      this.adPreviewIndexToTitle.push(genericTitle);
      this.adPreviewIndexToTitle.push(genericTitle);
    }
  }

  setupAdPreviewIndexToTitlesMapInViewAll(templateKey) {
    const adPreviewIndexToTitle = [];
    const responsivePreviewsTitles =
      _.map(this.aspectRatiosOrder, typeKey => this.props.i18n.getString(
        templateKey,
        {
          // eslint-disable-next-line
          ratio: this.props.i18n.getString(this.state.aspectRatios[typeKey].stringKey),
        }
      ));

    return adPreviewIndexToTitle.concat(responsivePreviewsTitles);
  }

  setupImagesAsync() {
    const imagesToSetup = [
      this.props.genericImage,
      ...this.props.placeHolderImages,
    ];

    const setupPromises = _.map(imagesToSetup, imageToSetup => getGenericImageObjectAsync(imageToSetup).then((imageObj) => {
      let croppedImages = getCroppedImage({
        originalImageToCrop: imageObj,
        isMultimediaAd: this.isMultimediaAd,
      });
      croppedImages = _.sortBy(croppedImages, image => this.aspectRatiosOrder.indexOf(image.type));
      _.each(croppedImages, (image) => {
        image.isIncluded = false; // eslint-disable-line no-param-reassign
      });
      return croppedImages;
    }));

    Promise.all(setupPromises).then(([defaultImages, placeHolderImages1, placeHolderImages2, placeHolderImages3]) => {
      this.setState({
        defaultImages,
        placeHolderImages: [placeHolderImages1 || defaultImages, placeHolderImages2 || defaultImages, placeHolderImages3 || defaultImages],
      });
    });
  }

  getAdPreviewTitle() {
    const { index } = this.state;

    return this.adPreviewIndexToTitle[index];
  }

  getAdPreviewButtonsForAdCreator(cellIndex = -1) {
    if (this.isMultimediaAd && !_.isEmpty(this.props.imageList)) {
      const { imageListForDisplay, index } = this.state;
      const selectedIndex = cellIndex > -1 ? cellIndex : index;
      const selectedImage = imageListForDisplay[selectedIndex];
      const isRequired = selectedImage && _.result(this.state.aspectRatios[selectedImage.type], 'minimumRequirements') > 0 && this.props.totalImageListCount === 1;
      const minSize = selectedImage && _.result(this.state.aspectRatios[selectedImage.type], 'minSize');
      const isSupported = selectedImage && this.state.aspectRatios[selectedImage.type];

      if (!isSupported) {
        return null;
      }

      if (this.isInvalidImageSize(selectedImage, minSize)) {
        return null;
      }

      return (
        <div className={classnames('ad-preview-actions', 'inline-title')}>
          {_.result(selectedImage, 'isIncluded') !== true ?
            (
              <button
                className="include-aspect-ratio-button btn btn-default"
                onClick={this.onAddAspectRatio}
                data-index={selectedIndex}
              >
                { this.props.i18n.getString('ImageEditor_Include_AspectRatio') }
              </button>
            ) :
            (
              <React.Fragment>
                {!_.result(selectedImage, 'disableEdit') &&
                  <button
                    className="crop-image-button btn btn-default"
                    onClick={this.onCrop}
                    data-index={selectedIndex}
                  >
                    {this.props.i18n.getString('ImageExperience_Edit_Aspect_Ratio')}
                  </button>
                }
                {isRequired &&
                  <TooltipHost
                    content={this.props.i18n.getString('ImageExperience_Image_Placement_Required_Message')}
                    calloutProps={{ gapSpace: 8 }}
                  >
                    <button
                      className="exclude-aspect-ratio-button btn btn-default"
                      onClick={this.onRemoveAspectRatio}
                      disabled
                      data-index={selectedIndex}
                    >
                      { this.props.i18n.getString('ImageEditor_Exclude_AspectRatio') }
                    </button>
                  </TooltipHost>
                }
                {!isRequired &&
                  <button
                    className="exclude-aspect-ratio-button btn btn-default"
                    onClick={this.onRemoveAspectRatio}
                    data-index={selectedIndex}
                  >
                    { this.props.i18n.getString('ImageEditor_Exclude_AspectRatio') }
                  </button>
                }
              </React.Fragment>
            )
          }
        </div>
      );
    } else if (this.props.isImageExtensionsEnhancementsEnabled && !_.isEmpty(this.props.imageList)) {
      return (
        <div>
          <button
            className={classnames('crop-image-button btn btn-default', this.props.customClasses.reCropButton)}
            onClick={this.onCrop}
            data-index={cellIndex}
          >
            { this.props.i18n.getString('ImageEditor_CropImage') }
          </button>
          <button
            className="replace-image-button btn btn-default"
            onClick={this.onReplace}
            data-index={cellIndex}
          >
            { this.props.i18n.getString('ImageExperience_Replace')}
          </button>
        </div>


      );
    }
    return null;
  }

  getAdPreviewActions() {
    if (this.isMultimediaAd && !_.isEmpty(this.props.imageList)) {
      const { imageListForDisplay, index } = this.state;
      const selectedImage = imageListForDisplay[index];
      const validationMsgElement = this.getValidationMessage(selectedImage);

      return (
        <div className="ad-preview-actions">
          {_.result(selectedImage, 'isIncluded') !== true ?
            (
              <React.Fragment>
                { validationMsgElement }
                { !validationMsgElement && !this.props.isAdCreatorEnabled &&
                  <React.Fragment>
                    <span className="action-label">
                      { this.props.i18n.getString('ImageEditor_Include_AspectRatio_Label') }
                    </span>
                    <button
                      className="include-aspect-ratio-button btn btn-default"
                      onClick={this.onAddAspectRatio}
                    >
                      { this.props.i18n.getString('ImageEditor_Include_AspectRatio') }
                    </button>
                  </React.Fragment>
                }
              </React.Fragment>
            ) :
            (
              !this.props.isAdCreatorEnabled &&
              <React.Fragment>
                <span className="action-label">
                  { _.result(selectedImage, 'isLogo') ?
                    this.props.i18n.getString('ImageEditor_CropLogo_Label') :
                    this.props.i18n.getString('ImageEditor_CropImage_Label') }
                </span>
                {!_.result(selectedImage, 'disableEdit') &&
                  <button
                    className="crop-image-button btn btn-default"
                    onClick={this.onCrop}
                  >
                    { this.props.i18n.getString('ImageEditor_CropImage') }
                  </button>
                }
                <button
                  className="exclude-aspect-ratio-button btn btn-default"
                  onClick={this.onRemoveAspectRatio}
                >
                  { this.props.i18n.getString('ImageEditor_Exclude_AspectRatio') }
                </button>
              </React.Fragment>
            )
          }
        </div>
      );
    }
    return null;
  }

  getAdPreviewHeader() {
    const enableActionBtn = this.isPlacementActionsEnabled();

    return this.props.isAdCreatorEnabled && this.isMultimediaAd ?
      (
        <div className="preview-header">
          <div className={classnames('ad-preview-title', 'with-additional-tag')}>
            {this.getAdPreviewTitle()}
          </div>
          <div className="flex-grow" />
          {
            enableActionBtn && this.getAdPreviewButtonsForAdCreator()
          }
        </div>
      ) : (
        <div className="ad-preview-title">
          {this.getAdPreviewTitle()}
        </div>
      );
  }

  getAdPreviewHeaderInternal({ title, index, enableActionBtn }) {
    const { imageListForDisplay } = this.state;
    const selectedImage = imageListForDisplay[index];
    const isSelectedImageIncluded = _.result(selectedImage, 'isIncluded');
    const minSize = selectedImage && this.state.aspectRatios[selectedImage.type].minSize;
    const hideActionBtn = !enableActionBtn
      || (!isSelectedImageIncluded && this.isInvalidImageSize(selectedImage, minSize));

    return (
      <div className="preview-header">
        <div className={classnames('ad-preview-title', 'with-additional-tag')}>
          {title}
        </div>
        <div className="flex-grow" />
        {!hideActionBtn &&
          <div className={classnames('ad-preview-actions', 'inline-title')}>
            {this.getAdPreviewButtonsForAdCreator(index)}
          </div>
        }
      </div>
    );
  }

  getValidationMessage(image) {
    if (_.result(image, 'isIncluded') !== true) {
      const isSupported = image && this.state.aspectRatios[image.type];
      const minSize = image && _.result(this.state.aspectRatios[image.type], 'minSize');

      if (!isSupported) {
        return (
          <span className="action-label">
            { this.props.i18n.getString('ImageEditor_Not_Supported') }
          </span>
        );
      }

      return this.isInvalidImageSize(image, minSize) &&
        <span className="action-label">
          {
            this.props.i18n.getString('ImageEditor_SizeValidationFailTemplate', {
              width: minSize[0],
              height: minSize[1],
            })
          }
        </span>;
    }

    return null;
  }

  setAutoState = (target) => {
    if (this.state.auto !== target) {
      if (target === false) {
        this.setState({
          auto: target,
        }, () => this.clearAutoRotateListener());
        return;
      }
      this.setState({
        auto: target,
      }, () => this.setAutoRotate());
    }
  };

  clearAutoRotateListener = () => {
    if (this.autoRotateListener) {
      clearTimeout(this.autoRotateListener);
      this.autoRotateListener = null;
    }
  };

  isInvalidImageSize(image, minSize) {
    if (!image) {
      return false;
    }

    let widthToCheck = image.Width;
    let heightToCheck = image.Height;

    if (this.props.isAdCreatorEnabled
      && image.BaseImage
      && image.BaseImage.CropSettings
      && image.BaseImage.CropSettings.SourceWidth > 0
      && image.BaseImage.CropSettings.SourceHeight > 0) {
      widthToCheck = image.BaseImage.CropSettings.SourceWidth;
      heightToCheck = image.BaseImage.CropSettings.SourceHeight;
    }

    return image && minSize && (widthToCheck < minSize[0] || heightToCheck < minSize[1]);
  }

  stop = () => {
    this.setAutoState(false);
  };

  start = () => {
    this.setAutoState(true);
  };

  handleSelect = (selectedIndex, e) => {
    this.setState({
      index: selectedIndex,
      direction: e.direction,
      auto: false,
    }, () => this.clearAutoRotateListener());
  };

  isPlacementActionsEnabled() {
    return this.props.totalImageListCount !== 0 && !this.props.disablePlacementActions;
  }

  render() {
    const { index, direction } = this.state;

    const { eligibleLayout } = this.props;
    const multiImages = this.state.multiImageList;
    const mobileMultiImages = multiImages && multiImages.length && multiImages.slice(0);
    const bingDesktopImages = multiImages && multiImages.length && multiImages.slice(0);
    let bingSingleImage = this.state.imageListForDisplay && this.state.imageListForDisplay.length &&
      _.find(this.state.imageListForDisplay, img => img.type === bingSingleImageAspR);
    bingSingleImage = bingSingleImage && bingSingleImage.clone();
    const enableActionBtn = this.isPlacementActionsEnabled();
    const enableViewAllBtn = enableActionBtn && (this.enableCreatorFeatures);

    return (
      <div className={classnames('new-preview-carousel-container', isPlayPausePreviewImageEnabled(this.props.permissions) ? this.props.classes.PlayPauseButtonsContainer : null)}>
        {this.getAdPreviewHeader()}
        {
          this.isRecommendationTypeImproveMultiMediaAdsEnabled
          && _.result(_.first(this.state.imageListForDisplay), 'isRecommendedImage') &&
          <div className="improve-mma-title-container">
            <span
              aria-label={this.props.i18n.getString('Ad_Strength_RSA_AI_Generated_Asset')}
              className="iconba iconba-Lightbulb new-preview-carousel"
            />
            <span>{this.props.newI18n.getString(_TL_('Recommended image'))}</span>
          </div>
        }
        {this.getAdPreviewActions()}
        <Carousel
          activeIndex={index}
          direction={direction}
          onSelect={this.handleSelect}
          controls={false}
          InjectedControls={isPlayPausePreviewImageEnabled(this.props.permissions) && this.props.enableAutoRotate ? <PlayPauseButtons
            newI18n={this.props.newI18n}
            isPlaying={this.state.auto}
            play={this.start}
            pause={this.stop}
            customizedClassName={classnames(this.props.classes.PlayPauseButtons, 'play-pause-mma')}
          /> : null}
          nextLabel={this.props.i18n.getString('ImageExperience_Ad_Preview_Next')}
          prevLabel={this.props.i18n.getString('ImageExperience_Ad_Preview_Previous')}
          className={classnames(this.enableCreatorFeatures ? 'new-carousel-navigation-mma' : '')}
        >
          {
            eligibleLayout !== 'Multi' && multiImages && multiImages.length > 0 && bingSingleImage &&
            <Carousel.Item>
              {getPreviewByType(
                bingSingleImage,
                this.props.previewTexts,
                this.props.i18n,
                true,
                'BingSingleImage'
              )}
            </Carousel.Item>
          }
          {
            eligibleLayout !== 'Single' && multiImages && multiImages.length > 0 &&
            <Carousel.Item>
              <MultiImageGeneral
                images={bingDesktopImages}
                i18n={this.props.i18n}
                previewTexts={this.props.previewTexts}
                previewType="BingDesktopImage"
              />
            </Carousel.Item>
          }
          {
            eligibleLayout !== 'Single' && multiImages && multiImages.length > 0 &&
            <Carousel.Item>
              <MultiImageGeneral
                images={mobileMultiImages}
                i18n={this.props.i18n}
                previewTexts={this.props.previewTexts}
                previewType="MobileMultiImage"
              />
            </Carousel.Item>
          }
          {
            eligibleLayout !== 'Multi' && this.state.imageListForDisplay.map((imageObj, arrIndex) => (
              <Carousel.Item key={imageObj.Type}>
                {getPreview(
                  imageObj,
                  this.props.previewTexts,
                  this.props.i18n,
                  index === arrIndex,
                  this.isMultimediaAd,
                  this.isMultiMediaAdsPhase2Enabled,
                  this.props.isAdCreatorEnabled
                )}
              </Carousel.Item>
            ))
          }
          {
            !this.isMultimediaAd && !this.props.isImageAdExtensionMode &&
            <Carousel.Item>
              <OutlookPreview
                i18n={this.props.i18n}
                previewTexts={this.props.previewTexts}
                isMobilePreview={false}
              />
            </Carousel.Item>
          }
          {
            !this.isMultimediaAd && !this.props.isImageAdExtensionMode &&
            <Carousel.Item>
              <OutlookPreview
                i18n={this.props.i18n}
                previewTexts={this.props.previewTexts}
                isMobilePreview
              />
            </Carousel.Item>
          }
        </Carousel>
        <div className="disclaimer-info">
          <div className="disclaimer-info-inline">
            {this.isMultimediaAd ? this.props.i18n.getString('ImagePreview_Disclaimer_Message_MultimediaAd') : this.props.i18n.getString('ImagePreview_Disclaimer_Message')}
          </div>
        </div>
        { enableViewAllBtn &&
          <ActionButton
            className={classnames('view-placements', this.props.customClasses.viewAllPlacementsButton)}
            onRenderIcon={() => (<span className="iconba iconba-FullView" />)}
            onClick={() => {
              const { viewAllPlacementsActivity } = getAdCreatorInstrumentation(this.props.instrumentation, this.props.adCreatorScenario); // eslint-disable-line max-len

              viewAllPlacementsActivity.trace('UserOpenedViewAllPlacements');
              this.props.onUsedViewAllPlacements();
              this.setState({ isViewAllPanelOpen: true });
            }}
          >
              { this.props.i18n.getString('ImageExperience_View_Placements_Example') }
          </ActionButton>
        }
        {/* This grid preview is another layout of carousel preview */}
        { enableViewAllBtn &&
          <StyledAdPlacementsGridPreviewPanel
            isOpen={this.state.isViewAllPanelOpen}
            onDismiss={() => {
              this.setState({ isViewAllPanelOpen: false });
            }}
            className={this.props.classes.gridPreviewContainer}
            cellsNumberPerRow={2}
            totalCount={_.size(this.state.imageListForDisplay)}
            cellHeight={510}
            onRenderCell={(cellIndex) => {
              const imageAsset = this.state.imageListForDisplay[cellIndex];

              return (
                <div className="placements-grid-preview-cell">
                  {this.getAdPreviewHeaderInternal({
                    title: this.adPreviewIndexToTitleInViewAll[cellIndex],
                    index: cellIndex,
                    enableActionBtn: true,
                  })}
                  {this.getValidationMessage(imageAsset)}
                  {getPreview(
                    imageAsset,
                    this.props.previewTexts,
                    this.props.i18n,
                    index === cellIndex,
                    this.isMultimediaAd,
                    this.isMultiMediaAdsPhase2Enabled,
                    this.props.isAdCreatorEnabled
                  )}
                </div>
              );
            }}
          />
        }
      </div>
    );
  }
}

UnStyledPreviewCarousel.propTypes = {
  previewTexts: PropTypes.shape({
    longHeadline: PropTypes.string,
    description: PropTypes.string,
    businessName: PropTypes.string,
  }).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
    parseDecimal: PropTypes.func.isRequired,
  }).isRequired,
  permissions: PropTypes.shape({
    IsMultiMediaAdsPhase2Enabled: PropTypes.bool,
  }),
  genericImage: PropTypes.string.isRequired,
  imageList: PropTypes.arrayOf(PropTypes.instanceOf(ImageExperienceImage)).isRequired,
  placeHolderImages: PropTypes.arrayOf(PropTypes.string),
  isImageAdExtensionMode: PropTypes.bool,
  eligibleLayout: PropTypes.string,
  isMultimediaAd: PropTypes.bool,
  onCropImage: PropTypes.func,
  onAddAspectRatio: PropTypes.func,
  onRemoveAspectRatio: PropTypes.func,
  isAdCreatorEnabled: PropTypes.bool,
  enableAutoRotate: PropTypes.bool,
  instrumentation: PropTypes.shape({
    addScenario: PropTypes.func,
  }),
  totalImageListCount: PropTypes.number,
  adCreatorScenario: PropTypes.shape({
    scenarioName: PropTypes.string,
    scenarioValue: PropTypes.string,
  }),
  classes: PropTypes.shape({
    gridPreviewContainer: PropTypes.string,
    PlayPauseButtons: PropTypes.string,
    PlayPauseButtonsContainer: PropTypes.string,
  }),
  disablePlacementActions: PropTypes.bool,
  onUsedViewAllPlacements: PropTypes.func,
  isImageExtensionsEnhancementsEnabled: PropTypes.bool,
  onReplaceImage: PropTypes.func,
  customClasses: PropTypes.shape({
    viewAllPlacementsButton: PropTypes.string,
    reCropButton: PropTypes.string,
  }),
  newI18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }),
};

UnStyledPreviewCarousel.defaultProps = {
  isImageAdExtensionMode: false,
  isMultimediaAd: false,
  eligibleLayout: '',
  placeHolderImages: [],
  onCropImage: _.noop,
  onReplaceImage: _.noop,
  onAddAspectRatio: _.noop,
  onRemoveAspectRatio: _.noop,
  permissions: {},
  enableAutoRotate: false,
  isAdCreatorEnabled: false,
  instrumentation: {
    addScenario: _.noop,
  },
  totalImageListCount: 0,
  adCreatorScenario: null,
  classes: {},
  disablePlacementActions: false,
  onUsedViewAllPlacements: _.noop,
  isImageExtensionsEnhancementsEnabled: false,
  customClasses: {},
  newI18n: {
    getString: _.noop,
  },
};

export const PreviewCarousel = memo(forwardRef((props, ref) => {
  const classes = useDefaultStyles(getPreviewCarouselStyle);
  return (<UnStyledPreviewCarousel ref={ref} {...props} classes={{ ...classes, ...props.classes }} />);
}));
